import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { api } from '@udm/contracts';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { createSite, createSiteFromProposal } from '../../api/client';
import { formatNumber } from '../../ui/format';
import { ErrorNote } from '../admin/ErrorNote';
import { useAdminSites } from '../sites/sitesData';

interface Plan {
  mode: 'client' | 'locality';
  parentId: string;
  name: string;
  area: 'osm' | 'draw';
  adopt: boolean;
}

/**
 * Superadmin: a client (or a locality of an existing client) for a proposed commune, created from the map card.
 * Area: the commune's OpenStreetMap boundary (editable later in the area editor) or drawn by hand. "Adopt" turns on
 * automatic adoption for the client — for a locality the client's area is extended by the commune first — so Central
 * adopts the discovered devices that already reported a position there at once (U12 re-evaluation), and later ones
 * as they connect. One request (POST /sites/from-proposal), one transaction on Central: the commune's exact boundary,
 * slivers along neighbouring areas trimmed and reported, a real overlap refused with nothing created (user report
 * 2026-10-10: the former sequence of calls left a client without area behind).
 */
export function CreateFromProposal({ p, onCancel }: { p: api.AdoptionProposal; onCancel: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const sites = useAdminSites();
  const clients = useMemo(
    () =>
      (sites.data?.items ?? [])
        .filter((s) => s.kind === 'organization' && s.status === 'active')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [sites.data],
  );
  const [plan, setPlan] = useState<Plan>({
    mode: 'client',
    parentId: '',
    name: p.name,
    area: 'osm',
    adopt: true,
  });
  const set = (patch: Partial<Plan>) => setPlan((cur) => ({ ...cur, ...patch }));
  const run = useMutation({
    mutationFn: async (x: Plan): Promise<api.SiteFromProposalResult> => {
      if (x.area === 'osm')
        return createSiteFromProposal({
          osm_ref: p.osm_ref,
          mode: x.mode,
          ...(x.mode === 'locality' ? { parent_id: x.parentId } : {}),
          name: x.name.trim(),
          adopt: x.adopt,
        });
      // drawn later in the area editor: the site alone (one call)
      const site = await createSite({
        parent_id: x.mode === 'client' ? null : x.parentId,
        kind: x.mode === 'client' ? 'organization' : 'locality',
        name: x.name.trim(),
      });
      return { site, trimmed: [] };
    },
    onSettled: () =>
      Promise.all(
        [
          ['admin', 'sites'],
          ['admin', 'site-areas'],
          ['admin', 'adoption-proposals'],
          ['admin', 'discovered'],
        ].map((queryKey) => qc.invalidateQueries({ queryKey })),
      ),
  });

  const site = run.data?.site;
  const trimmed = run.data?.trimmed ?? [];
  if (run.isSuccess && site)
    return (
      <div className="proposal-create" role="status">
        <span>
          {plan.area === 'osm' && plan.adopt
            ? t('map.proposals.create.doneAdopt', { name: site.name, count: p.devices })
            : t('map.proposals.create.done', { name: site.name })}
        </span>
        {site.siruta_code && (
          <span className="small muted">
            {t('sites.uat.created', { name: site.uat_name, county: site.county, siruta: site.siruta_code })}
          </span>
        )}
        {trimmed.length > 0 && (
          <span className="small muted">
            {t('map.proposals.create.trimmed', {
              list: trimmed.map((x) => `${x.name} (${formatNumber(x.area_m2)} m²)`).join(', '),
            })}
          </span>
        )}
        <div className="toolbar">
          <Link to={`/sites/${site.id}/area`} className="btn">
            {t(plan.area === 'osm' ? 'map.proposals.create.editArea' : 'map.proposals.create.drawArea')}
          </Link>
          <Link to="/sites" state={{ open: site.id }} className="btn btn-ghost">
            {t('map.proposals.create.openSite')}
          </Link>
        </div>
      </div>
    );

  const valid = plan.name.trim().length > 0 && (plan.mode === 'client' || plan.parentId !== '');
  return (
    <form
      className="proposal-create"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) run.mutate(plan);
      }}
    >
      <strong>{t('map.proposals.create.title', { name: p.name })}</strong>
      <div className="segmented" role="radiogroup" aria-label={t('map.proposals.create.as')}>
        {(['client', 'locality'] as const).map((m) => (
          <label key={m} className="check-row">
            <input type="radio" name="mode" checked={plan.mode === m} onChange={() => set({ mode: m })} />
            {t(`map.proposals.create.mode.${m}`)}
          </label>
        ))}
      </div>
      {plan.mode === 'locality' && (
        <select
          className="input"
          aria-label={t('map.proposals.create.client')}
          value={plan.parentId}
          onChange={(e) => set({ parentId: e.target.value })}
        >
          <option value="">{t('map.proposals.create.client')}</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      )}
      <input
        className="input"
        aria-label={t('map.proposals.create.name')}
        value={plan.name}
        onChange={(e) => set({ name: e.target.value })}
      />
      <fieldset className="proposal-create-area">
        <legend className="small muted">{t('map.proposals.create.area')}</legend>
        <label className="check-row">
          <input
            type="radio"
            name="area"
            checked={plan.area === 'osm'}
            onChange={() => set({ area: 'osm' })}
          />
          {t('map.proposals.create.areaOsm', { name: p.name })}
        </label>
        <label className="check-row">
          <input
            type="radio"
            name="area"
            checked={plan.area === 'draw'}
            onChange={() => set({ area: 'draw' })}
          />
          {t('map.proposals.create.areaDraw')}
        </label>
      </fieldset>
      <label className="check-row">
        <input
          type="checkbox"
          checked={plan.area === 'osm' && plan.adopt}
          disabled={plan.area !== 'osm'}
          onChange={(e) => set({ adopt: e.target.checked })}
        />
        {t('map.proposals.create.adopt', { count: p.devices })}
      </label>
      {plan.area === 'osm' && plan.adopt && (
        <span className="small muted">
          {t(
            plan.mode === 'client'
              ? 'map.proposals.create.adoptHint'
              : 'map.proposals.create.adoptHintLocality',
          )}
        </span>
      )}
      <ErrorNote error={run.error} />
      <div className="toolbar">
        <button type="submit" className="btn btn-primary" disabled={!valid || run.isPending}>
          {run.isPending ? t('common.loading') : t('map.proposals.create.submit')}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
